import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { FiChevronRight, FiChevronLeft, FiX, FiSave, FiAlertCircle } from 'react-icons/fi';
import apiService from '../../services/api';
import logger from '../../utils/logger';

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const Container = styled.div`
  background: var(--background-primary);
  border-radius: 16px;
  width: 90%;
  max-width: 1000px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.3);
  border: 1px solid var(--border-color);
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem;
  border-bottom: 1px solid var(--border-color);
  position: sticky;
  top: 0;
  background: var(--background-primary);
  z-index: 10;

  h2 {
    margin: 0;
    font-size: 1.5rem;
    font-weight: 700;
    color: var(--text-primary);
    letter-spacing: -0.02em;
  }
`;

const CloseButton = styled.button`
  background: transparent;
  border: none;
  border-radius: 12px;
  color: var(--text-secondary);
  cursor: pointer;
  padding: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    color: var(--text-primary);
    background: var(--background-secondary);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &:focus-visible {
    outline: 2px solid var(--focus-ring);
    outline-offset: 2px;
  }
`;

const Content = styled.div`
  padding: 2rem;

  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const AlertBox = styled.div`
  display: flex;
  gap: 0.75rem;
  padding: 1rem;
  margin-bottom: 1.5rem;
  border-radius: 12px;
  border: 1px solid;
  align-items: flex-start;

  &.info {
    background: rgba(33, 150, 243, 0.1);
    border-color: #2196F3;
    color: #2196F3;

    svg {
      color: #2196F3;
      flex-shrink: 0;
      margin-top: 0.125rem;
    }
  }

  span {
    font-size: 0.875rem;
    font-weight: 500;
    line-height: 1.5;
  }
`;

const PanelContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 100px 1fr;
  gap: 1.5rem;
  margin-bottom: 2rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
`;

const Panel = styled.div`
  display: flex;
  flex-direction: column;
  border: 1px solid var(--border-color);
  background: var(--background-secondary);
  border-radius: 12px;
  overflow: hidden;
  box-shadow: var(--shadow-sm);
`;

const PanelHeader = styled.div`
  padding: 1rem 1.25rem;
  background: var(--background-tertiary);
  border-bottom: 1px solid var(--border-color);
  font-weight: 600;
  color: var(--text-primary);
  font-size: 0.875rem;
  letter-spacing: -0.01em;
`;

const ItemList = styled.div`
  flex: 1;
  overflow-y: auto;
  max-height: 400px;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: var(--background-secondary);
  }

  &::-webkit-scrollbar-thumb {
    background: var(--border-color);
    border-radius: 3px;

    &:hover {
      background: var(--text-secondary);
    }
  }
`;

const ItemCard = styled.div`
  padding: 0.875rem 1rem;
  border-bottom: 1px solid var(--border-color);
  display: flex;
  gap: 0.75rem;
  align-items: center;
  transition: all 0.2s ease;
  cursor: pointer;
  user-select: none;

  &:hover {
    background: var(--background-primary);
  }

  &.selected {
    background: rgba(216, 184, 137, 0.1);
    border-left: 3px solid var(--primary-color);
  }

  &:last-child {
    border-bottom: none;
  }

  input[type='checkbox'] {
    cursor: pointer;
    flex-shrink: 0;
    width: 18px;
    height: 18px;
    accent-color: var(--primary-color);
  }
`;

const ItemInfo = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  .item-name {
    font-weight: 500;
    font-size: 0.875rem;
    color: var(--text-primary);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    line-height: 1.4;
  }

  .item-type {
    font-size: 0.75rem;
    color: var(--text-secondary);
    text-transform: uppercase;
    margin-top: 0.25rem;
    font-weight: 600;
    letter-spacing: 0.05em;
  }
`;

const ItemDetails = styled.div`
  flex: 1;
  min-width: 0;
`;

const Badge = styled.span`
  display: inline-block;
  padding: 0.25rem 0.625rem;
  background: rgba(255, 152, 0, 0.15);
  color: #ff9800;
  font-size: 0.65rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  border-radius: 4px;
  white-space: nowrap;
  flex-shrink: 0;
`;

const ControlsCenter = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 0.75rem;

  @media (max-width: 768px) {
    flex-direction: row;
    justify-content: center;
  }
`;

const ControlButton = styled.button`
  background: var(--primary-color);
  border: none;
  color: var(--text-inverse);
  padding: 0.875rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 12px;
  transition: all 0.2s ease;
  box-shadow: var(--shadow-sm);
  min-width: 48px;
  min-height: 48px;

  &:hover:not(:disabled) {
    background: var(--primary-dark);
    transform: translateY(-1px);
    box-shadow: var(--shadow-md);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none !important;
  }

  &:focus-visible {
    outline: 2px solid var(--focus-ring);
    outline-offset: 2px;
  }

  svg {
    width: 20px;
    height: 20px;
  }
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 0.75rem;
  justify-content: flex-end;
  padding-top: 1.5rem;
  border-top: 1px solid var(--border-color);
  margin-top: 1rem;

  @media (max-width: 480px) {
    flex-direction: column-reverse;
    gap: 0.75rem;
  }
`;

const Button = styled.button`
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 12px;
  font-weight: 600;
  font-size: 0.875rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  transition: all 0.2s ease;
  font-family: 'Inter', sans-serif;

  &.primary {
    background: var(--primary-color);
    color: var(--text-inverse);
    box-shadow: var(--shadow-sm);

    &:hover:not(:disabled) {
      background: var(--primary-dark);
      transform: translateY(-1px);
      box-shadow: var(--shadow-md);
    }
  }

  &.secondary {
    background: var(--background-primary);
    color: var(--text-primary);
    border: 1px solid var(--border-color);

    &:hover:not(:disabled) {
      background: var(--background-secondary);
      border-color: var(--border-dark);
    }
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none !important;
  }

  &:focus-visible {
    outline: 2px solid var(--focus-ring);
    outline-offset: 2px;
  }

  @media (max-width: 480px) {
    width: 100%;
    justify-content: center;
  }
`;

const FilterContainer = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-bottom: 1rem;
  flex-wrap: wrap;
`;

const FilterButton = styled.button`
  padding: 0.625rem 1.25rem;
  border: 1px solid ${props => props.active ? 'var(--primary-color)' : 'var(--border-color)'};
  background: ${props => props.active ? 'var(--primary-color)' : 'var(--background-primary)'};
  color: ${props => props.active ? 'var(--text-inverse)' : 'var(--text-primary)'};
  cursor: pointer;
  border-radius: 999px;
  font-size: 0.8125rem;
  font-weight: 600;
  transition: all 0.2s ease;
  font-family: 'Inter', sans-serif;
  box-shadow: ${props => props.active ? 'var(--shadow-sm)' : 'none'};

  &:hover {
    border-color: var(--primary-color);
    ${props => !props.active ? 'background: var(--background-secondary);' : ''}
    ${props => props.active ? 'background: var(--primary-dark);' : ''}
    ${props => props.active ? 'transform: translateY(-1px);' : ''}
    ${props => props.active ? 'box-shadow: var(--shadow-md);' : ''}
  }

  &:focus-visible {
    outline: 2px solid var(--focus-ring);
    outline-offset: 2px;
  }
`;

/**
 * Component for assigning gradebook items to rubric categories
 * Allows two-way assignment of quizzes, assignments, and attendance
 */
function GradebookItemAssigner({ categoryId, categoryName, rubricId, courseId, onSave, onCancel }) {
  const [availableItems, setAvailableItems] = useState({ quizzes: [], assignments: [], attendance: null });
  const [assignedItems, setAssignedItems] = useState([]);
  const [selectedAvailable, setSelectedAvailable] = useState(new Set());
  const [selectedAssigned, setSelectedAssigned] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [filterType, setFilterType] = useState('all');

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      // Get available items
      const availResp = await apiService.request(
        `/rubrics/categories/${categoryId}/available-items`,
        { method: 'GET' }
      );
      if (availResp.success) {
        setAvailableItems(availResp.data);
      }

      // Get assigned items
      const assignResp = await apiService.request(
        `/rubrics/categories/${categoryId}/items`,
        { method: 'GET' }
      );
      if (assignResp.success) {
        setAssignedItems(assignResp.data);
      }
    } catch (err) {
      logger.error('Error loading gradebook items:', err);
      setError('Failed to load gradebook items');
    } finally {
      setLoading(false);
    }
  }, [categoryId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const getFilteredAvailable = () => {
    let items = [];

    if (filterType === 'all' || filterType === 'quiz') {
      items = [...items, ...availableItems.quizzes.map(q => ({ ...q, type: 'quiz' }))];
    }
    if (filterType === 'all' || filterType === 'assignment') {
      items = [...items, ...availableItems.assignments.map(a => ({ ...a, type: 'assignment' }))];
    }
    if ((filterType === 'all' || filterType === 'attendance') && availableItems.attendance) {
      items = [...items, { ...availableItems.attendance, type: 'attendance' }];
    }

    return items;
  };

  const toggleAvailableSelection = (itemId, itemType) => {
    const key = `${itemType}_${itemId}`;
    const newSelected = new Set(selectedAvailable);
    if (newSelected.has(key)) {
      newSelected.delete(key);
    } else {
      newSelected.add(key);
    }
    setSelectedAvailable(newSelected);
  };

  const toggleAssignedSelection = (assignmentId) => {
    const newSelected = new Set(selectedAssigned);
    if (newSelected.has(assignmentId)) {
      newSelected.delete(assignmentId);
    } else {
      newSelected.add(assignmentId);
    }
    setSelectedAssigned(newSelected);
  };

  const moveToAssigned = async () => {
    if (selectedAvailable.size === 0) return;

    try {
      const itemsToAssign = Array.from(selectedAvailable).map(key => {
        const [type, id] = key.split('_');
        return {
          gradebook_item_id: parseInt(id),
          gradebook_item_type: type
        };
      });

      const resp = await apiService.request(
        `/rubrics/categories/${categoryId}/assign-items`,
        {
          method: 'POST',
          body: JSON.stringify({ items: itemsToAssign })
        }
      );

      if (resp.success) {
        await loadData();
        setSelectedAvailable(new Set());
      } else {
        setError(resp.message || 'Failed to assign items');
      }
    } catch (err) {
      logger.error('Error assigning items:', err);
      setError('Failed to assign items');
    }
  };

  const removeFromAssigned = async () => {
    if (selectedAssigned.size === 0) return;

    try {
      for (const assignmentId of selectedAssigned) {
        const assignment = assignedItems.find(a => a.id === assignmentId);
        if (assignment) {
          await apiService.request(
            `/rubrics/categories/${categoryId}/items/${assignment.gradebook_item_id}?itemType=${assignment.gradebook_item_type}`,
            { method: 'DELETE' }
          );
        }
      }
      await loadData();
      setSelectedAssigned(new Set());
    } catch (err) {
      logger.error('Error removing items:', err);
      setError('Failed to remove items');
    }
  };

  const filteredAvailable = getFilteredAvailable();

  return (
    <Overlay onClick={onCancel}>
      <Container onClick={e => e.stopPropagation()}>
        <Header>
          <h2>Assign Gradebook Items to {categoryName}</h2>
          <CloseButton onClick={onCancel}>
            <FiX size={24} />
          </CloseButton>
        </Header>

        <Content>
          {error && (
            <AlertBox className="info">
              <FiAlertCircle size={20} />
              <span>{error}</span>
            </AlertBox>
          )}

          <AlertBox className="info">
            <FiAlertCircle size={20} />
            <span>Select gradebook items to assign to this category. Students' scores from these items will be used to calculate the final grade.</span>
          </AlertBox>

          <FilterContainer>
            <FilterButton
              active={filterType === 'all'}
              onClick={() => setFilterType('all')}
            >
              All Items
            </FilterButton>
            <FilterButton
              active={filterType === 'quiz'}
              onClick={() => setFilterType('quiz')}
            >
              Quizzes
            </FilterButton>
            <FilterButton
              active={filterType === 'assignment'}
              onClick={() => setFilterType('assignment')}
            >
              Assignments
            </FilterButton>
            <FilterButton
              active={filterType === 'attendance'}
              onClick={() => setFilterType('attendance')}
            >
              Attendance
            </FilterButton>
          </FilterContainer>

          <PanelContainer>
            {/* Available Items Panel */}
            <Panel>
              <PanelHeader>Available Items ({filteredAvailable.length})</PanelHeader>
              <ItemList>
                {filteredAvailable.length === 0 ? (
                  <ItemCard style={{ cursor: 'default', justifyContent: 'center', padding: '2rem' }}>
                    <span style={{ color: 'var(--text-secondary)', fontSize: '0.875rem', fontWeight: 500 }}>No items available</span>
                  </ItemCard>
                ) : (
                  filteredAvailable.map(item => (
                    <ItemCard
                      key={`${item.type}_${item.id}`}
                      className={selectedAvailable.has(`${item.type}_${item.id}`) ? 'selected' : ''}
                      onClick={() => toggleAvailableSelection(item.id, item.type)}
                    >
                      <input
                        type="checkbox"
                        checked={selectedAvailable.has(`${item.type}_${item.id}`)}
                        onChange={() => toggleAvailableSelection(item.id, item.type)}
                      />
                      <ItemInfo>
                        <ItemDetails>
                          <div className="item-name">{item.name || item.title}</div>
                          <div className="item-type">{item.type}</div>
                        </ItemDetails>
                        {item.source === 'external' && <Badge>External</Badge>}
                      </ItemInfo>
                    </ItemCard>
                  ))
                )}
              </ItemList>
            </Panel>

            {/* Control Buttons */}
            <ControlsCenter>
              <ControlButton
                onClick={moveToAssigned}
                disabled={selectedAvailable.size === 0}
                title={selectedAvailable.size === 0 ? 'Select items to assign' : 'Assign selected items'}
              >
                <FiChevronRight size={20} />
              </ControlButton>
              <ControlButton
                onClick={removeFromAssigned}
                disabled={selectedAssigned.size === 0}
                title={selectedAssigned.size === 0 ? 'Select items to remove' : 'Remove selected items'}
              >
                <FiChevronLeft size={20} />
              </ControlButton>
            </ControlsCenter>

            {/* Assigned Items Panel */}
            <Panel>
              <PanelHeader>Assigned Items ({assignedItems.length})</PanelHeader>
              <ItemList>
                {assignedItems.length === 0 ? (
                  <ItemCard style={{ cursor: 'default', justifyContent: 'center', padding: '2rem' }}>
                    <span style={{ color: 'var(--text-secondary)', fontSize: '0.875rem', fontWeight: 500 }}>No items assigned</span>
                  </ItemCard>
                ) : (
                  assignedItems.map(item => (
                    <ItemCard
                      key={item.id}
                      className={selectedAssigned.has(item.id) ? 'selected' : ''}
                      onClick={() => toggleAssignedSelection(item.id)}
                    >
                      <input
                        type="checkbox"
                        checked={selectedAssigned.has(item.id)}
                        onChange={() => toggleAssignedSelection(item.id)}
                      />
                      <ItemInfo>
                        <ItemDetails>
                          <div className="item-name">{item.item_name || item.name || `Item #${item.gradebook_item_id}`}</div>
                          <div className="item-type">{item.gradebook_item_type}</div>
                        </ItemDetails>
                        {item.source === 'external' && <Badge>External</Badge>}
                      </ItemInfo>
                    </ItemCard>
                  ))
                )}
              </ItemList>
            </Panel>
          </PanelContainer>

          <ActionButtons>
            <Button className="secondary" onClick={onCancel} disabled={loading}>
              <FiX size={16} />
              Cancel
            </Button>
            <Button
              className="primary"
              onClick={onSave}
              disabled={loading}
            >
              <FiSave size={16} />
              {loading ? 'Saving...' : 'Done'}
            </Button>
          </ActionButtons>
        </Content>
      </Container>
    </Overlay>
  );
}

export default GradebookItemAssigner;
